import React, { useEffect, useState } from 'react';
import { hqBrandService, HqBrand, HqBrandUpdate } from '../services/hqBrandService';
import { hqAuthService } from '../services/hqAuthService';

const HqBrandPage: React.FC = () => {
  const me = hqAuthService.getCachedUser();
  const isAdmin = me?.role === 'hq_admin';

  const [brand, setBrand] = useState<HqBrand | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<HqBrandUpdate>({});
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const b = await hqBrandService.get();
      setBrand(b);
      setForm({
        name: b.name,
        business_number: b.business_number || '',
        contact_email: b.contact_email || '',
        contact_phone: b.contact_phone || '',
        address: b.address || '',
        royalty_rate: b.royalty_rate || 0,
      });
    } catch (e: any) {
      setError(e?.response?.data?.detail || '본부 정보를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await hqBrandService.update(form);
      setEditing(false);
      await load();
    } catch (e: any) {
      alert(e?.response?.data?.detail || '저장 실패');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="text-slate-400">불러오는 중...</div>;
  }

  if (error || !brand) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4">
        {error || '본부 정보가 없습니다.'}
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-slate-800">본부 정보</h1>
        {isAdmin && !editing && (
          <button
            onClick={() => setEditing(true)}
            className="px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-lg"
          >
            수정
          </button>
        )}
      </div>

      <div className="bg-white rounded-xl shadow p-6">
        {editing ? (
          <form onSubmit={handleSave} className="space-y-4">
            <Field label="본부명" value={form.name || ''} onChange={(v) => setForm({ ...form, name: v })} required />
            <Field label="사업자등록번호" value={form.business_number || ''} onChange={(v) => setForm({ ...form, business_number: v })} />
            <Field label="대표 이메일" type="email" value={form.contact_email || ''} onChange={(v) => setForm({ ...form, contact_email: v })} />
            <Field label="대표 전화번호" value={form.contact_phone || ''} onChange={(v) => setForm({ ...form, contact_phone: v })} />
            <Field label="주소" value={form.address || ''} onChange={(v) => setForm({ ...form, address: v })} />
            <Field
              label="로열티율 (%)"
              type="number"
              value={String(form.royalty_rate ?? 0)}
              onChange={(v) => setForm({ ...form, royalty_rate: Number(v) || 0 })}
            />

            <div className="flex gap-2 justify-end pt-4">
              <button
                type="button"
                onClick={() => setEditing(false)}
                className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-lg disabled:bg-slate-400"
              >
                {submitting ? '저장 중...' : '저장'}
              </button>
            </div>
          </form>
        ) : (
          <dl className="space-y-3">
            <Row label="본부명" value={brand.name} />
            <Row label="사업자등록번호" value={brand.business_number || '-'} />
            <Row label="대표 이메일" value={brand.contact_email || '-'} />
            <Row label="대표 전화번호" value={brand.contact_phone || '-'} />
            <Row label="주소" value={brand.address || '-'} />
            <Row label="로열티율" value={`${brand.royalty_rate ?? 0}%`} />
            <Row label="가맹점 PII 공유 동의" value={brand.pii_sharing_agreed_at ? '동의함' : '미동의 (집계만)'} />
            <Row label="등록일" value={brand.created_at ? new Date(brand.created_at).toLocaleString('ko-KR') : '-'} />
          </dl>
        )}
      </div>
    </div>
  );
};

const Row: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex border-b pb-2 last:border-b-0">
    <dt className="w-40 text-sm text-slate-500">{label}</dt>
    <dd className="flex-1 text-slate-800">{value}</dd>
  </div>
);

interface FieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
}
const Field: React.FC<FieldProps> = ({ label, value, onChange, type = 'text', required }) => (
  <div>
    <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      className="w-full px-3 py-2 border rounded-lg"
    />
  </div>
);

export default HqBrandPage;
